/**
 * Line-style industry icons for the homepage "Built for your industry"
 * section. Clean strokes, single blue gradient, no fills — matches the
 * Salesforce / HubSpot / Monday.com aesthetic the client requested.
 *
 * All icons share the same 96x96 viewBox, gradient, and stroke width so
 * the 6-card grid reads as a cohesive set.
 */
import { memo } from "react";

// A single reusable gradient definition — defined once per page render.
// Rendered inside every SVG so the stroke picks it up.
function IconGradient({ id }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"   stopColor="#3b82f6" />
        <stop offset="100%" stopColor="#1e3a8a" />
      </linearGradient>
    </defs>
  );
}

const commonStrokeProps = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 2,
};

function SvgFrame({ children, testId, gradientId }) {
  return (
    <svg
      viewBox="0 0 96 96"
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      data-testid={testId}
    >
      <IconGradient id={gradientId} />
      <g stroke={`url(#${gradientId})`} {...commonStrokeProps}>
        {children}
      </g>
    </svg>
  );
}

/* --- Distributors: storefront with awning, door, windows --- */
export const DistributorsIcon = memo(function DistributorsIcon() {
  return (
    <SvgFrame testId="industry-icon-distributors" gradientId="grad-dist">
      {/* Awning */}
      <path d="M12 28 L84 28 L78 40 L18 40 Z" />
      {/* Awning scallop lines */}
      <line x1="24" y1="28" x2="22" y2="40" />
      <line x1="36" y1="28" x2="34" y2="40" />
      <line x1="48" y1="28" x2="48" y2="40" />
      <line x1="60" y1="28" x2="62" y2="40" />
      <line x1="72" y1="28" x2="74" y2="40" />
      {/* Building body */}
      <path d="M18 40 L18 82 L78 82 L78 40" />
      {/* Ground line */}
      <line x1="10" y1="82" x2="86" y2="82" />
      {/* Left window */}
      <rect x="26" y="50" width="16" height="16" rx="1.5" />
      {/* Door */}
      <path d="M52 82 L52 58 L68 58 L68 82" />
      <circle cx="64" cy="72" r="1.2" />
    </SvgFrame>
  );
});

/* --- Suppliers: hand truck / dolly with stacked boxes --- */
export const SuppliersIcon = memo(function SuppliersIcon() {
  return (
    <SvgFrame testId="industry-icon-suppliers" gradientId="grad-supp">
      {/* Handle */}
      <path d="M18 14 L26 14 L26 76" />
      {/* Toe plate */}
      <path d="M26 76 L52 76" />
      {/* Wheel */}
      <circle cx="32" cy="82" r="6" />
      {/* Boxes stacked (2) */}
      <rect x="34" y="46" width="30" height="22" rx="1" />
      <line x1="49" y1="46" x2="49" y2="68" />
      <line x1="34" y1="57" x2="64" y2="57" />
      {/* Top box */}
      <rect x="40" y="28" width="26" height="18" rx="1" />
      <line x1="53" y1="28" x2="53" y2="46" />
      {/* Tape */}
      <path d="M40 36 L66 36" />
    </SvgFrame>
  );
});

/* --- Decorators: polo/tee shirt with printhead above and chest mark --- */
export const DecoratorsIcon = memo(function DecoratorsIcon() {
  return (
    <SvgFrame testId="industry-icon-decorators" gradientId="grad-dec">
      {/* Print head assembly above shirt */}
      <rect x="34" y="6" width="28" height="8" rx="2" />
      <line x1="42" y1="14" x2="42" y2="20" />
      <line x1="54" y1="14" x2="54" y2="20" />
      {/* Shirt body outline: shoulders -> body */}
      <path d="M20 32 L36 24 L40 30 L56 30 L60 24 L76 32 L70 44 L64 38 L64 84 L32 84 L32 38 L26 44 Z" />
      {/* Collar V */}
      <path d="M40 30 L48 40 L56 30" />
      {/* Chest decoration mark */}
      <circle cx="48" cy="58" r="6" />
      <path d="M45 58 L48 61 L52 55" />
    </SvgFrame>
  );
});

/* --- Print Shops: wide-format printer with a printed sheet coming out --- */
export const PrintShopsIcon = memo(function PrintShopsIcon() {
  return (
    <SvgFrame testId="industry-icon-print-shops" gradientId="grad-print">
      {/* Top intake */}
      <rect x="18" y="14" width="60" height="6" rx="1" />
      {/* Printer body */}
      <rect x="12" y="20" width="72" height="26" rx="3" />
      {/* Status dots */}
      <circle cx="22" cy="33" r="1.5" />
      <circle cx="28" cy="33" r="1.5" />
      {/* Legs */}
      <line x1="20" y1="46" x2="20" y2="54" />
      <line x1="76" y1="46" x2="76" y2="54" />
      {/* Emerging sheet */}
      <path d="M22 46 L22 82 L74 82 L74 46" />
      {/* Sheet artwork: mountain + sun */}
      <circle cx="34" cy="60" r="3" />
      <path d="M30 74 L40 62 L50 72 L60 60 L68 74" />
      {/* Sheet baseline */}
      <line x1="22" y1="74" x2="74" y2="74" />
    </SvgFrame>
  );
});

/* --- Sign Companies: billboard with two top spotlights + mountain scene --- */
export const SignCompaniesIcon = memo(function SignCompaniesIcon() {
  return (
    <SvgFrame testId="industry-icon-sign-companies" gradientId="grad-sign">
      {/* Spotlights */}
      <path d="M28 8 L28 12 L24 18 L32 18 Z" />
      <path d="M68 8 L68 12 L64 18 L72 18 Z" />
      <line x1="28" y1="18" x2="28" y2="22" />
      <line x1="68" y1="18" x2="68" y2="22" />
      {/* Billboard frame */}
      <rect x="12" y="22" width="72" height="42" rx="2" />
      {/* Scene inside: sun + mountains */}
      <circle cx="30" cy="36" r="3" />
      <path d="M14 60 L28 44 L38 54 L52 40 L66 54 L82 42" />
      {/* Post */}
      <line x1="48" y1="64" x2="48" y2="86" />
      {/* Base */}
      <path d="M36 86 L60 86" />
    </SvgFrame>
  );
});

/* --- QuickBooks Users: monitor on stand w/ green QB badge + bar chart --- */
export const QuickBooksIcon = memo(function QuickBooksIcon() {
  return (
    <SvgFrame testId="industry-icon-quickbooks" gradientId="grad-qb">
      {/* Monitor frame */}
      <rect x="10" y="14" width="76" height="52" rx="3" />
      {/* Screen inner (subtle inset) */}
      <rect x="14" y="18" width="68" height="44" rx="1.5" />
      {/* QB badge circle on screen (green fill accent) */}
      <circle cx="30" cy="38" r="10" fill="#059669" stroke="#059669" />
      {/* Bar chart inside monitor */}
      <line x1="50" y1="54" x2="50" y2="42" />
      <line x1="58" y1="54" x2="58" y2="34" />
      <line x1="66" y1="54" x2="66" y2="28" />
      <line x1="74" y1="54" x2="74" y2="38" />
      {/* Baseline */}
      <line x1="46" y1="54" x2="78" y2="54" />
      {/* Stand */}
      <line x1="42" y1="66" x2="42" y2="76" />
      <line x1="54" y1="66" x2="54" y2="76" />
      <line x1="34" y1="76" x2="62" y2="76" />
      {/* QB wordmark rendered as text but styled to look clean */}
      <text
        x="30" y="42"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto"
        fontWeight="700"
        fontSize="10"
        fill="#ffffff"
        stroke="none"
      >
        qb
      </text>
    </SvgFrame>
  );
});
